import { useEffect, useRef } from "react";
import { EXCHANGE_ACCOUNT_DISCONNECTION_REASONS, type SUPPORTED_EXCHANGES } from "@cede/types";
import { API_ENDPOINT } from "@cede/utils";
import { useHubUser } from "./useHubUser";
import { useApi } from "@/hooks/core/useApi";
import { useKrakenReconnectRequiredStore } from "@/store/useKrakenReconnectRequiredStore";
import { useSubAccountRemovedStore } from "@/store/useSubAccountRemovedStore";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

// Predates the Kraken notices; kept so already-seen ids still count.
const SEEN_DISCONNECTIONS_STORAGE_KEY = "alreadySeenSubAccountDisconnections";
const NEW_DISCONNECTIONS_QUERY_KEY = "newExchangeAccountDisconnections";

const SeenDisconnectionsSchema = z.array(z.number());

const getSeenDisconnections = () => {
  try {
    const seenDisconnections = localStorage.getItem(SEEN_DISCONNECTIONS_STORAGE_KEY);
    if (!seenDisconnections) {
      return [];
    }
    const rawValue = JSON.parse(seenDisconnections);
    const { success, data } = SeenDisconnectionsSchema.safeParse(rawValue);
    if (!success) {
      return [];
    }
    return data;
  } catch {
    return [];
  }
};

const markDisconnectionsSeen = (disconnectionIds: number[]) => {
  const seenDisconnections = new Set([...getSeenDisconnections(), ...disconnectionIds]);
  try {
    localStorage.setItem(SEEN_DISCONNECTIONS_STORAGE_KEY, JSON.stringify(Array.from(seenDisconnections)));
  } catch {
    // Storage full or blocked: still show the notice, it just shows again next visit.
  }
};

export const useDetectExchangeAccountDisconnection = () => {
  const { auth, hubUser } = useHubUser();
  const api = useApi();
  const { setOpen: setSubAccountRemovedOpen, setExchangeIds } = useSubAccountRemovedStore();
  const { setOpen: setKrakenReconnectRequiredOpen } = useKrakenReconnectRequiredStore();
  const noticeShownForHubUserId = useRef<number | null>(null);

  const { data: alreadySeenDisconnections } = useQuery({
    queryKey: [SEEN_DISCONNECTIONS_STORAGE_KEY],
    queryFn: async () => getSeenDisconnections(),
  });

  const { data: newDisconnections } = useQuery({
    queryKey: [NEW_DISCONNECTIONS_QUERY_KEY, hubUser?.id],
    queryFn: async () => {
      const seenIds = new Set(alreadySeenDisconnections);
      const disconnections = await api.getWithAuth(auth, API_ENDPOINT.HUB_EXCHANGE_ACCOUNT_DISCONNECTION);
      return disconnections.filter((disconnection) => !seenIds.has(disconnection.id));
    },
    enabled: !!hubUser && !!alreadySeenDisconnections,
  });

  // Writes to the modal stores, so it runs after render, only for the signed-in user's own data.
  useEffect(() => {
    if (!hubUser || !newDisconnections?.length || noticeShownForHubUserId.current === hubUser.id) return;
    const krakenDisconnections = newDisconnections.filter(
      (disconnection) => disconnection.reason === EXCHANGE_ACCOUNT_DISCONNECTION_REASONS.KRAKEN_TX_HISTORY_REQUIRED,
    );
    const subAccountDisconnections = newDisconnections.filter(
      (disconnection) => disconnection.reason === EXCHANGE_ACCOUNT_DISCONNECTION_REASONS.SUB_ACCOUNT_REMOVED_BY_ADMIN,
    );
    // One modal per session: the ones not shown stay unseen for the next visit.
    if (krakenDisconnections.length > 0) {
      markDisconnectionsSeen(krakenDisconnections.map((disconnection) => disconnection.id));
      setKrakenReconnectRequiredOpen(true);
    } else if (subAccountDisconnections.length > 0) {
      markDisconnectionsSeen(subAccountDisconnections.map((disconnection) => disconnection.id));
      setSubAccountRemovedOpen(true);
      setExchangeIds(subAccountDisconnections.map((disconnection) => disconnection.exchangeId as SUPPORTED_EXCHANGES));
    }
    noticeShownForHubUserId.current = hubUser.id;
  }, [hubUser, newDisconnections, setKrakenReconnectRequiredOpen, setSubAccountRemovedOpen, setExchangeIds]);
};
